// Fetch manuales desde Contentful
export const fetchManuales = async () => {
  const response = await contentfulClient.getEntries({
    content_type: "manualDoc",
    order: "fields.orden",
  });
  return response.items.map((item) => {
    const fields = item.fields || {};
    return {
      id: item.sys.id,
      titulo: fields.titulo || "",
      descripcion: fields.descripcion || "",
      imagen: fields.imagen?.fields?.file?.url ? `https:${fields.imagen.fields.file.url}` : null,
      link: fields.link || null,
      pdf: fields.pdf?.fields?.file?.url ? `https:${fields.pdf.fields.file.url}` : null,
      orden: fields.orden || 0,
    };
  });
};
import contentfulClient from "./contentfulClient";

/**
 * Obtiene los menús del sistema.
 * @returns {Promise<Array>} Lista de menús.
 */
export const fetchMenus = async () => {
  try {
    // First, try to fetch a siteMenu (new content type) which contains an ordered
    // reference array `menuItems` pointing to `menu` entries. This lets editors
    // reorder menus by dragging references in the CMS.
    const siteResp = await contentfulClient.getEntries({
      content_type: "siteMenu",
      include: 2,
      limit: 1,
    });

    // Helper to map a menu entry (content type `menu`) into the shape Header expects
    const mapMenuEntry = (entry, includedAssets = [], includedEntries = []) => {
      if (!entry || !entry.fields) return null;
      const fields = entry.fields || {};

      const resolveSub = (sub) => {
        if (!sub) return null;
        // sub may be an Entry object or a Link
        let subEntry = sub;
        if (sub.sys && sub.sys.linkType === "Entry" && !sub.fields) {
          subEntry = includedEntries.find((e) => e.sys?.id === sub.sys.id) || sub;
        }
        const imageId = subEntry?.fields?.imagen?.sys?.id;
        const imageAsset = includedAssets.find((a) => a.sys?.id === imageId);
        const imageUrl = imageAsset?.fields?.file?.url
          ? `https:${imageAsset.fields.file.url}`
          : subEntry?.fields?.imagen?.fields?.file?.url
          ? `https:${subEntry.fields.imagen.fields.file.url}`
          : null;

        return {
          name: subEntry?.fields?.nombre || subEntry?.fields?.title || "",
          description: subEntry?.fields?.descripcion || "",
          link: subEntry?.fields?.link || "",
          image: imageUrl,
        };
      };

      return {
        name: fields.nombre || fields.title || "",
        type: fields.tipo || null,
        link: fields.link || null,
        subMenus: (fields.subMenus || []).map(resolveSub).filter(Boolean),
      };
    };

    if (siteResp.items && siteResp.items.length > 0) {
      const site = siteResp.items[0];
      const includedAssets = siteResp.includes?.Asset || [];
      const includedEntries = siteResp.includes?.Entry || [];

      // menuItems is an ordered array of Links to `menu` entries
      const refs = site.fields.menuItems || [];
      const menus = refs
        .map((ref) => {
          // find the actual entry in includes
          const id = ref?.sys?.id;
          const menuEntry = includedEntries.find((e) => e.sys?.id === id) || null;
          // if it's not included, we could fetch it individually, but prefer to skip
          return mapMenuEntry(menuEntry, includedAssets, includedEntries);
        })
        .filter(Boolean);

      // Return the ordered menus defined in siteMenu
      return menus;
    }

    // Fallback: old behavior, fetch all `menu` entries and map them (unordered)
    const response = await contentfulClient.getEntries({
      content_type: "menu",
      include: 1,
    });
    const assets = response.includes?.Asset || [];

    return response.items.map((item) => ({
      name: item.fields.nombre,
      type: item.fields.tipo,
      link: item.fields.link || null,
      subMenus: item.fields.subMenus
        ? item.fields.subMenus.map((sub) => {
            const imageId = sub.fields.imagen?.sys.id;
            const imageAsset = assets.find((asset) => asset.sys.id === imageId);
            const imageUrl = imageAsset?.fields.file.url
              ? `https:${imageAsset.fields.file.url}`
              : null;

            return {
              name: sub.fields.nombre,
              description: sub.fields.descripcion ||"",
              link: sub.fields.link || "",
              image: imageUrl,
            };
          })
        : [],
    }));
  } catch (error) {
    return [];
  }
};


/**
 * Obtiene la configuración global del sistema.
 * @returns {Promise<Object>} Configuración global (teléfono, logo, etc.).
 */
export const fetchGlobalConfig = async () => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "configuracionGlobal", // ID del modelo de configuración global
      limit: 1, // Solo necesitamos una entrada
    });

    if (response.items.length > 0) {
      const fields = response.items[0].fields;
      return {
        nombre: fields.nombreDeLaOrganizacion || '',
        direccion: fields.direccion || '',
        email: fields.email || '',
        telefono: fields.telefono || '',
        logo: fields.logo?.fields?.file?.url || '',
        logoSinFondo: fields.logoSinFondo?.fields?.file?.url || '',
        logoSevac: fields.logoSevac?.fields?.file?.url || '',
        page404: fields.noPage?.fields?.file?.url || '',
        facebook: fields.facebook || '',
        instagram: fields.instagram || '',
        twitter: fields.twitter || '',
        ubicacion: fields.ubicacion || '',
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};


export const fetchNoticias = async () => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "noticia",
      include: 2, // Asegura que los assets de imágenes estén anidados correctamente
    });

    return response.items.map((item) => {
      // Verificar si la imagen está directamente en el campo fields.imagen
      const imageUrl = item.fields.imagen?.fields?.file?.url
        ? `https:${item.fields.imagen.fields.file.url}`
        : null;

      return {
        id: item.sys.id,
        titulo: item.fields.titulo || "Sin título",
        descripcion: item.fields.descripcion || "",
        imagen: imageUrl, // Usa la URL de imagen si está disponible
        slug: item.fields.slug,
        textoDelBoton: item.fields.textoDelBoton,
        categoria: item.fields.categoria || null,
        pdf: item.fields.pdf?.fields?.file?.url ? `https:${item.fields.pdf.fields.file.url}` : null,
        fechaInicio: item.fields.fecha || null,
        fechaFinalizacion: item.fields.fechaFinalizacion || null,
        status: item.fields.status || null,
      };
    });
  } catch (error) {
    return [];
  }
};


export const fetchNoticiaPorSlug = async (slug) => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "noticia",
      "fields.slug": slug,
      limit: 1,
      include: 2, // Mayor profundidad para obtener imágenes y relaciones
    });

    if (response.items.length === 0) {
      return null;
    }

    const noticia = response.items[0].fields;

    return {
      titulo: noticia.titulo,
      descripcion: noticia.descripcion,
      cuerpo: noticia.cuerpo,
      fecha: noticia.fecha || "Fecha no disponible",
      imagen: noticia.imagen?.fields.file.url
        ? `https:${noticia.imagen.fields.file.url}`
        : null,
      pdf: noticia.pdf?.fields.file.url
        ? `https:${noticia.pdf.fields.file.url}`
        : null,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene los colores globales desde Contentful (content type: configuracionGlobal).
 * @returns {Promise<{ primary: string, secondary: string, tertiary: string }>} Colores globales.
 */
export async function fetchGlobalConfigColors() {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "configuracionGlobal",
      limit: 1,
    });

    if (!response.items.length) {
      throw new Error("No se encontró la configuración global en Contentful.");
    }

    const fields = response.items[0].fields;

    // Los campos pueden venir localizados, así que accedemos a 'en-US' o el locale que uses
    const getColor = (field, fallback) =>
      typeof field === "object" && field !== null
        ? field["en-US"] || fallback
        : field || fallback;

    return {
      primary: getColor(fields.colorPrimario, "#003366"),
      secondary: getColor(fields.colorSecundario, "#72CCF4"),
      tertiary: getColor(fields.colorTerciario, "#F86B16"),
      fondosSecundarios: getColor (fields.fondosSecundarios,"#F7F8FA"),
    };
  } catch (error) {
    // Devuelve valores por defecto si hay error
    return {
      primary: "#003366",
      secondary: "#72CCF4",
      tertiary: "#F86B16",
    };
  }
}
/**
 * Obtiene los resultados SEvac desde Contentful.
 * @returns {Promise<Array>} Lista de resultados [{ titulo, ano, trimestre, pdf }]
 */
export const fetchResultadosSevac = async (tipo = "Sevac Entry") => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "resultadoSevac",
      order: "-fields.ano,-fields.trimestre",
      include: 2, // Para traer los assets referenciados
    });

    return response.items
      .filter((item) => {
        const itemTipo = item.fields.tipo;
        return tipo === "Sevac Entry"
          ? !itemTipo || itemTipo === "Sevac Entry"
          : itemTipo === tipo;
      })
      .map((item) => ({
      id: item.sys.id,
      titulo: item.fields.titulo,
      ano: item.fields.ano,
      trimestre: item.fields.trimestre,
      documentos: (item.fields.pdf || []).map(asset => ({
        nombre: asset.fields?.title || "Documento",
        pdf: asset.fields?.file?.url
          ? `https:${asset.fields.file.url}`
          : null,
      })),
      }));
  } catch (error) {
    return [];
  }
};

export const fetchPaginaPrincipal = async () => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "paginaPrincipal",
      include: 3, // Profundidad suficiente para obtener referencias
    });

    if (response.items.length > 0) {
      const pagina = response.items[0].fields;

      // Procesamos todos los elementos como "componentes"
      const componentes = pagina.elementosDeReferencia.map((componente) => {
        const tipo = componente.sys.contentType.sys.id;

        if (tipo === "bloqueInteractivo") {
          return {
            tipo,
            titulo: componente.fields.titulo || "Sin título",
            elementos: (componente.fields.elementos || []).map((elemento) => ({
              titulo: elemento.fields.titulo || "Sin título",
              contenido: elemento.fields.contenido || null,
              background: elemento.fields.background?.fields?.file?.url
                ? `https:${elemento.fields.background.fields.file.url}`
                : null,
              icono: elemento.fields.image?.fields?.file?.url
                ? `https:${elemento.fields.image.fields.file.url}`
                : null,
              colorTexto: elemento.fields.colorTexto || null,
              componente: elemento.fields.componente
                ? {
                    tipo: elemento.fields.componente.sys?.contentType?.sys?.id || null,
                    ...(elemento.fields.componente.fields || {}),
                  }
                : null,
            })),
          };
        }

        if (tipo === "IconsContainer") {
          return {
            tipo,
            titulo: componente.fields.titulo || "Sin título",
            iconos: (componente.fields.logos || []).map((icono) => ({
              titulo: icono.fields.title || "Sin título",
              icono: icono.fields.image?.fields?.file?.url
                ? `https:${icono.fields.image.fields.file.url}`
                : null,
              link: icono.fields.link || null,
            })),
          };
        }

        if (tipo === "imageCarouselBlock") {
          // Resolver assets de imagen si vienen como links
          const assets = response.includes?.Asset || [];
          const imgs = (componente.fields.imagenes || []).map((img) => {
            if (!img) return null;
            if (img.fields) return img; // ya resuelto
            const id = img.sys?.id;
            return assets.find((a) => a.sys?.id === id) || null;
          }).filter(Boolean);

          return {
            tipo: 'imageCarouselBlock',
            images: imgs,
            description: componente.fields.description || componente.fields.descripcion || null,
            background: componente.fields.colorDeFondo || componente.fields.background || null,
          };
        }

        if (tipo === "galeriaDeImagenes") {
          // Resolver assets de imagen si vienen como links (similar a imageCarouselBlock)
          const assets = response.includes?.Asset || [];
          const imgs = (componente.fields.imagenes || []).map((img) => {
            if (!img) return null;
            if (img.fields) return img; // ya resuelto
            const id = img.sys?.id;
            return assets.find((a) => a.sys?.id === id) || null;
          }).filter(Boolean);

          return {
            tipo: 'galeriaDeImagenes',
            images: imgs,
            titulo: componente.fields.titulo || componente.fields.title || null,
          };
        }

        // Nuevo: cardsInformativas como componente embebido en la página
        if (tipo === "cardsInformativas") {
          // Si las tarjetas están incluidas, resolverlas aquí para evitar fetch adicional
          const tarjetas = componente.fields.tarjetas || [];
          const included = response.includes || {};
          const includedEntries = included.Entry || [];

          const resolveEntry = (link) => {
            if (!link) return null;
            if (link.fields) return link;
            const id = link.sys?.id;
            return includedEntries.find((e) => e.sys?.id === id) || null;
          };

          const items = tarjetas
            .map((t) => resolveEntry(t))
            .filter(Boolean)
            .map((entry) => ({
              id: entry.sys.id,
              titulo: entry.fields?.titulo || "",
              contenido: entry.fields?.descripcion || null, // RichText
            }));

          return {
            tipo: 'cardsInformativas',
            titulo: componente.fields.titulo || null,
            group: {
              titulo: componente.fields.titulo || null,
              items,
            },
          };
        }

        if (tipo === "tarjetasRapidas") {
          return {
            tipo: "quickCardsComponent",
            titulo: componente.fields.titulo || "Sin título",
            cards: (componente.fields.tarjetas || []).map((card) => {
              const iconoUrl = card.fields.icono?.fields?.file?.url
                ? `https:${card.fields.icono.fields.file.url}`
                : null;
        
              return {
                titulo: card.fields.titulo || "Sin título",
                descripcion: card.fields.descripcion || "",
                icono: iconoUrl,
                informacionExtra: card.fields.informacionExtra || "",
                ruta: card.fields.ruta || "#",
              };
            }),
          };
        }

      return null;
    }).filter(Boolean); // Filtra elementos nulos

      return {
        titulo: pagina.titulo || "Sin título",
        componentes, // Ahora todos son componentes, incluidos los bloques interactivos
        // Soporte opcional para altura del carrusel (en px) definida en la entrada paginaPrincipal
        carruselHeightPx:
          typeof pagina.carruselHeightPx !== 'undefined'
            ? Number(pagina.carruselHeightPx)
            : typeof pagina.alturaCarrusel !== 'undefined'
            ? Number(pagina.alturaCarrusel)
            : null,
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene un grupo de tarjetas informativas (cardsInformativas) y sus items referenciados.
 * Se puede filtrar por slug si el content type tiene ese campo.
 * Devuelve: { titulo, items: [{ id, titulo, subtitulo, contenido }] }
 */
export const fetchCardsGroup = async (slug) => {
  try {
    const query = {
      content_type: "cardsInformativas",
      include: 3,
      limit: 1,
    };
    if (slug) query["fields.slug"] = slug;

    const response = await contentfulClient.getEntries(query);
    if (!response.items.length) return null;

    const groupEntry = response.items[0];
    const group = groupEntry.fields || {};

    // Tarjetas se almacenan en el campo 'tarjetas' según el content model
    const tarjetas = group.tarjetas || [];

    // Helper para resolver una referencia a entry si viene como Link
    const findIncludedEntry = (id) => {
      const includes = response.includes || {};
      const entries = includes.Entry || [];
      return entries.find((e) => e.sys?.id === id) || null;
    };

    const items = tarjetas.map((t) => {
      // t puede ser ya una Entry con fields, o un Link { sys: { id } }
      let entry = t;
      if (t.sys && t.sys.linkType === 'Entry' && !t.fields) {
        entry = findIncludedEntry(t.sys.id) || t;
      }
      const f = entry.fields || {};
      return {
        id: entry.sys?.id,
        titulo: f.titulo || f.title || "",
        subtitulo: f.subtitulo || f.subtitle || "",
        // descripcion es RichText en el content type
        contenido: f.descripcion || f.contenido || f.description || null,
      };
    });

    return {
      titulo: group.titulo || group.title || null,
      items,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene el organigrama desde Contentful.
 * Se espera un content type llamado `orgNodo` donde cada entrada puede tener
 * - nombre (string)
 * - puesto (string)
 * - descripcion (text)
 * - foto (Asset)
 * - children (Array de referencias a orgNodo)
 *
 * La función reconstruye el árbol a partir de las referencias.
 */
export const fetchOrganigrama = async () => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: "orgNodo",
      include: 3,
    });

    const items = response.items || [];
    const assets = response.includes?.Asset || [];

    // Mapear id -> nodo (mapeo de campos: cargo -> nombre, zona -> puesto, responsable -> descripcion)
    const map = {};
    items.forEach((item) => {
      const f = item.fields || {};
      // foto puede venir resuelta o solo como link; buscamos en includes si hace falta
      let fotoUrl = null;
      if (f.foto?.fields?.file?.url) {
        fotoUrl = `https:${f.foto.fields.file.url}`;
      } else if (f.foto?.sys?.id) {
        const asset = assets.find((a) => a.sys?.id === f.foto.sys.id);
        if (asset?.fields?.file?.url) fotoUrl = `https:${asset.fields.file.url}`;
      }

      map[item.sys.id] = {
        id: item.sys.id,
        nombre: f.cargo || "",
        puesto: f.zona || "",
        descripcion: f.responsable || null,
        foto: fotoUrl,
        childrenRefs: Array.isArray(f.children) ? f.children.map((r) => r.sys.id) : [],
        children: [],
      };
    });

    // Rellenar children y detectar raíces (nodos no referenciados por nadie)
    const referenced = new Set();
    Object.values(map).forEach((n) => n.childrenRefs.forEach((id) => referenced.add(id)));

    Object.values(map).forEach((n) => {
      n.children = (n.childrenRefs || []).map((cid) => map[cid]).filter(Boolean);
      delete n.childrenRefs;
    });

    const roots = Object.values(map).filter((n) => !referenced.has(n.id));

    // Si hay más de una raíz, devolver un pseudo-root con esos roots como children
    if (roots.length === 1) return roots[0];
    if (roots.length > 1) {
      return {
        nombre: "ROOT",
        puesto: "",
        foto: null,
        children: roots,
      };
    }

    // Si no hay raíces, pero hay items, devolver el primero convertido
    if (items.length > 0) {
      const first = map[items[0].sys.id];
      return first || null;
    }

    return null;
  } catch (error) {
    return null;
  }
};


